import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Paper from "@material-ui/core/Paper";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Button from "@material-ui/core/Button";
import Collapse from "@material-ui/core/Collapse";
import EyeIcon from "mdi-material-ui/EyeCircle";
import moment from "moment";
import { withSnackbar } from "notistack";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";

import LoadingButton from "../../components/Buttons/Loading";
import ActivityDialog from "../../components/Dialogs/ActivityReport";
import AlertDialog from "../../components/Dialogs/Alert";

// Mapping cache for optimized filtering
class MappingCache {
  constructor() {
    this.propertyMap = new Map(); // name -> [id1, id2, ...]
    this.userMap = new Map();     // name -> [id1, id2, ...]
    this.loaded = false;
    this.loading = false;
  }

  async ensureLoaded(serviceProviderId, properties, users) {
    if (this.loaded || this.loading) return;

    this.loading = true;

    try {
      // Build property map from existing properties data
      Object.values(properties).forEach(prop => {
        const name = prop.displayName.toLowerCase();
        if (!this.propertyMap.has(name)) {
          this.propertyMap.set(name, []);
        }
        this.propertyMap.get(name).push(prop.propertyId);
      });

      // Build user map from existing users data
      Object.values(users).forEach(user => {
        const name = user.displayName.toLowerCase();
        if (!this.userMap.has(name)) {
          this.userMap.set(name, []);
        }
        this.userMap.get(name).push(user.userId);
      });

      this.loaded = true;
      console.log(`Mapping cache loaded: ${this.propertyMap.size} properties, ${this.userMap.size} users`);
    } catch (error) {
      console.error('Error loading mapping cache:', error);
    } finally {
      this.loading = false;
    }
  }

  findPropertyIds(searchTerm) {
    const results = [];
    const term = searchTerm.toLowerCase();

    for (const [name, ids] of this.propertyMap) {
      if (name.includes(term)) {
        results.push(...ids);
      }
    }
    return results;
  }

  findUserIds(searchTerm) {
    const results = [];
    const term = searchTerm.toLowerCase();

    for (const [name, ids] of this.userMap) {
      if (name.includes(term)) {
        results.push(...ids);
      }
    }
    return results;
  }

  clear() {
    this.propertyMap.clear();
    this.userMap.clear();
    this.loaded = false;
    this.loading = false;
  }
}
import IncidentDialog from "../../components/Dialogs/IncidentReport";
import ResponseDialog from "../../components/Dialogs/Response";
import ReportsTable from "../../components/Tables/Reports";
import Header from "../../components/Typography/Header";
import {
  property as propConst,
  missingValue,
  moment as momentConst,
} from "../../constants";
import {
  getServiceProvider,
  markAsNotProcessed,
  markAsProcessed,
  queryIncidentReportsByServiceProviderId as queryIncidents,
  queryPatrolsByServiceProviderId as queryPatrols,
  queryResponsesByServiceProviderId as queryResponses,
  queryPatrolsByServiceProviderIdPaginated,
  queryIncidentReportsByServiceProviderIdPaginated,
  queryResponsesByServiceProviderIdPaginated,
  queryPatrolsByPropertyIdStartTimeIndex,
  queryIncidentReportsByPropertyIdStartTimeIndex,
  queryInstancesByPropertyIdStartTimeIndex,
} from "../../utils/appsyncOperations";
import { createReportContent } from "../../utils/downloadReports";
import { appsyncError } from "../../utils/errors";
import sendReports from "../../utils/sendReports";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const maxReports = 1000;
const BATCH_SIZE = 50; // Number of items to load per batch
const MAX_CACHE_SIZE = 5; // Maximum number of cached searches

// Global mapping cache instance
const mappingCache = new MappingCache();

const ReportsContent = ({
  serviceProviderId,
  properties,
  users,
  clients,
  enqueueSnackbar,
}) => {
  const [items, setItems] = useState([]);
  const [dailyActivityId, setDailyActivityId] = useState(null);
  const [incidentId, setIncidentId] = useState(null);
  const [responseId, setResponseId] = useState(null);
  const [showSendClientReportAlert, setShowSendClientReportAlert] = useState(
    false
  );
  const [clientReportLoading, setClientReportLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [markAsProcessedLoading, setMarkAsProcessedLoading] = useState(false);
  const [markAsNotProcessedLoading, setMarkAsNotProcessedLoading] = useState(
    false
  );
  const [startDate, setStartDate] = useState(
    () => new Date(moment().startOf("day").valueOf())
  );
  const [endDate, setEndDate] = useState(() => new Date());
  const [data, setData] = useState([]);
  const [patrolData, setPatrols] = useState({});
  const [responseData, setResponses] = useState({});
  const [incidentData, setIncidents] = useState({});
  const [input, setInput] = useState({
    patrols: [],
    responses: [],
    incidents: [],
  });

  // Progressive loading state
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [paginationTokens, setPaginationTokens] = useState({
    patrols: null,
    incidents: null,
    responses: null,
  });
  const [totalCounts, setTotalCounts] = useState({
    patrols: 0,
    incidents: 0,
    responses: 0,
  });

  // Data caching
  const [dataCache, setDataCache] = useState({});
  const [cacheKey, setCacheKey] = useState(null);

  // Export mode and filters
  const [exportMode, setExportMode] = useState(false);
  const [exportFilters, setExportFilters] = useState({
    propertyName: '',
    userName: '',
    reportType: 'all', // 'all', 'patrols', 'incidents', 'responses'
  });

  // Cache management function
  const manageCache = (newCacheKey, newData) => {
    setDataCache(prev => {
      const newCache = { ...prev, [newCacheKey]: newData };

      // Remove oldest cache entries if we exceed the limit
      const cacheKeys = Object.keys(newCache);
      if (cacheKeys.length > MAX_CACHE_SIZE) {
        const oldestKey = cacheKeys[0];
        delete newCache[oldestKey];
      }

      return newCache;
    });
  };

  // Helper function to generate PDF using the same logic as manual download
  // Optimized query functions using propertyId indexes
  const queryPatrolsOptimized = async (filters, from, to) => {
    await mappingCache.ensureLoaded(serviceProviderId, properties, users);

    const propertyIds = filters.propertyName
      ? mappingCache.findPropertyIds(filters.propertyName)
      : null;

    const userIds = filters.userName
      ? mappingCache.findUserIds(filters.userName)
      : null;

    // Early return if no matches
    if (propertyIds && propertyIds.length === 0) return { items: [], nextToken: null };
    if (userIds && userIds.length === 0) return { items: [], nextToken: null };

    if (propertyIds && propertyIds.length <= 20) {
      // Query each property individually using propertyId index - VERY EFFICIENT
      const promises = propertyIds.map(propertyId =>
        queryPatrolsByPropertyIdStartTimeIndex({
          propertyId,
          startTime: from,
          to
        })
      );

      const results = await Promise.all(promises);
      let allPatrols = results.flatMap(r => r.items);

      // Apply user filtering if needed
      if (userIds) {
        allPatrols = allPatrols.filter(p => userIds.includes(p.senderId));
      }

      return { items: allPatrols, nextToken: null };
    } else {
      // Fall back to service provider query + filtering
      const patrols = await queryPatrolsByServiceProviderIdPaginated({
        serviceProviderId,
        from,
        to
      });

      let filtered = patrols.items;
      if (propertyIds) filtered = filtered.filter(p => propertyIds.includes(p.propertyId));
      if (userIds) filtered = filtered.filter(p => userIds.includes(p.senderId));

      return { items: filtered, nextToken: patrols.nextToken };
    }
  };

  const queryIncidentsOptimized = async (filters, from, to) => {
    await mappingCache.ensureLoaded(serviceProviderId, properties, users);

    const propertyIds = filters.propertyName
      ? mappingCache.findPropertyIds(filters.propertyName)
      : null;

    const userIds = filters.userName
      ? mappingCache.findUserIds(filters.userName)
      : null;

    if (propertyIds && propertyIds.length === 0) return { items: [], nextToken: null };
    if (userIds && userIds.length === 0) return { items: [], nextToken: null };

    if (propertyIds && propertyIds.length <= 20) {
      // Query each property individually using propertyId index
      const promises = propertyIds.map(propertyId =>
        queryIncidentReportsByPropertyIdStartTimeIndex({
          propertyId,
          from,
          to
        })
      );

      const results = await Promise.all(promises);
      let allIncidents = results.flatMap(r => r.items);

      if (userIds) {
        allIncidents = allIncidents.filter(i => userIds.includes(i.senderId));
      }

      return { items: allIncidents, nextToken: null };
    } else {
      // Fall back to service provider query + filtering
      const incidents = await queryIncidentReportsByServiceProviderIdPaginated({
          serviceProviderId,
          from,
        to
      });

      let filtered = incidents.items;
      if (propertyIds) filtered = filtered.filter(i => propertyIds.includes(i.propertyId));
      if (userIds) filtered = filtered.filter(i => userIds.includes(i.senderId));

      return { items: filtered, nextToken: incidents.nextToken };
    }
  };

  const queryInstancesOptimized = async (filters, from, to) => {
    await mappingCache.ensureLoaded(serviceProviderId, properties, users);

    const propertyIds = filters.propertyName
      ? mappingCache.findPropertyIds(filters.propertyName)
      : null;

    // Note: Instances don't have senderId field, so user filtering is not supported
    // const userIds = filters.userName
    //   ? mappingCache.findUserIds(filters.userName)
    //   : null;

    if (propertyIds && propertyIds.length === 0) return { items: [], nextToken: null };

    if (propertyIds && propertyIds.length <= 20) {
      // Query each property individually using propertyId-createdAt index
      const promises = propertyIds.map(propertyId =>
        queryInstancesByPropertyIdStartTimeIndex({
          propertyId,
          from,
          to
        })
      );

      const results = await Promise.all(promises);
      let allInstances = results.flatMap(r => r.items);

      // Note: User filtering not supported for instances (no senderId field)

      return { items: allInstances, nextToken: null };
    } else {
      // Fall back to service provider query + filtering
      const instances = await queryResponsesByServiceProviderIdPaginated({
          serviceProviderId,
          from,
        to
      });

      let filtered = instances.items;
      if (propertyIds) filtered = filtered.filter(i => propertyIds.includes(i.propertyId));
      // Note: User filtering not supported for instances (no senderId field)

      return { items: filtered, nextToken: instances.nextToken };
    }
  };

  const generatePDF = async (patrols, incidents, responses, fileName) => {
    try {
      const serviceProvider = await getServiceProvider(serviceProviderId);
      const content = await createReportContent({
        dars: Array.isArray(patrols) ? patrols : [],
        endDate,
        incidents: Array.isArray(incidents) ? incidents : [],
        properties,
        responses: Array.isArray(responses) ? responses : [],
        serviceProvider,
        startDate,
        timeFormat: momentConst.format,
      });

      const docDefinition = {
        content,
        defaultStyle: {
          font: "Roboto",
        },
        footer: (currentPage, pageCount) => {
          if (currentPage === 1) {
            return null;
          }
          return {
            alignment: "center",
            fontSize: 10,
            margin: 16,
            text: `${currentPage} of ${pageCount}`,
          };
        },
        header: (currentPage) => {
          if (currentPage === 1) {
            return null;
          }
          return {
            alignment: "center",
            bold: true,
            fontSize: 10,
            margin: 16,
            text: serviceProvider.fullName,
          };
        },
        pageBreakBefore: (currentNode, followingNodesOnPage) => {
          return (
            currentNode.startPosition.pageNumber !== 1 &&
            currentNode.image &&
            followingNodesOnPage.length <= 3
          );
        },
        styles: {
          dateRange: {
            margin: [0, 0, 0, 8],
          },
          header: {
            fontSize: 18,
            bold: true,
            margin: [4, 0],
          },
          picture: {
            margin: [0, 8, 0, 8],
          },
          pictureDescription: {
            margin: [50, 0, 50, 8],
          },
          pictureTimestamp: {
            bold: true,
            fontSize: 10,
            margin: [50, 0, 50, 4],
          },
          title: {
            fontSize: 24,
            bold: true,
            margin: [0, 0, 0, 8],
          },
          table: {
            margin: [0, 8, 0, 16],
          },
          tableHeader: {
            bold: true,
            margin: [4, 8],
          },
          tableItem: {
            margin: [4, 8],
          },
          subheader: {
            fontSize: 14,
            margin: [4, 0, 4, 8],
          },
          subheaderTwo: {
            fontSize: 14,
            margin: [4, 8, 4, 0],
          },
          tocHeadingOne: {
            margin: [0, 8],
          },
          reportLine: {
            fontSize: 10,
            margin: [0, 1, 0, 1],
            color: '#333333',
          },
          messageLine: {
            fontSize: 9,
            margin: [0, 1, 0, 1],
            color: '#666666',
          },
        },
      };

      return new Promise((resolve, reject) => {
      pdfMake
        .createPdf(docDefinition)
          .download(fileName, (error) => {
            if (error) {
              console.error('PDF generation error:', error);
              reject(error);
            } else {
              resolve();
            }
          });
      });
    } catch (error) {
      console.error('Error in generatePDF:', error);
      throw error;
    }
  };
  const variantConst = propConst.variant;
  const classes = useStyles();

  // Helper function to process and format data for display
  const processDataForDisplay = (patrols, incidents, responses) => {
    const momentFormat = "Y/MM/DD \u2022 HH:mm:ss";
    const tempData = [];

    // Process patrols
    tempData.push(
      ...patrols
        .filter(
          (patrol) => properties[patrol.propertyId] && users[patrol.senderId]
        )
        .map((patrol) => {
          const variant = properties[patrol.propertyId].variant;
          const type =
            patrol.isVacationWatch || patrol.isCardinal
              ? "VIP Report"
              : "Daily Activity";
          return {
            type,
            userDisplayName: users[patrol.senderId].displayName,
            propertyDisplayName: properties[patrol.propertyId].displayName,
            timestamp: moment(patrol.startTime).format(momentFormat),
            formattedAddress:
              properties[patrol.propertyId].place.formattedAddress,
            id: patrol.patrolId,
            variant: variant
              ? variantConst[variant].text
              : variantConst.NONE.text,
            actions: actions(patrol.patrolId, type),
            processed: patrol.processed ? "Yes" : "No",
          };
        })
    );

    // Process incidents
    const filteredIncidents = incidents.filter(
      (incident) =>
        properties[incident.propertyId] && users[incident.submitted.by]
    );

    tempData.push(
      ...filteredIncidents.map((incident) => {
        const variant = properties[incident.propertyId].variant;
        const type = "Incident";
        return {
          type,
          userDisplayName: users[incident.submitted.by].displayName,
          propertyDisplayName: properties[incident.propertyId].displayName,
          timestamp: moment(incident.startTime).format(momentFormat),
          formattedAddress:
            properties[incident.propertyId].place.formattedAddress,
          id: incident.incidentId,
          variant: variant
            ? variantConst[variant].text
            : variantConst.NONE.text,
          actions: actions(incident.incidentId, type),
          processed: incident.processed ? "Yes" : "No",
        };
      })
    );

    // Process responses
    tempData.push(
      ...responses
        .filter((response) => properties[response.propertyId])
        .map((response) => {
          const variant = properties[response.propertyId].variant;
          const type = "Response";
          return {
            type,
            userDisplayName: missingValue,
            propertyDisplayName: properties[response.propertyId].displayName,
            timestamp: moment(response.createdAt).format(momentFormat),
            formattedAddress:
              properties[response.propertyId].place.formattedAddress,
            id: response.instanceId,
            variant: variant
              ? variantConst[variant].text
              : variantConst.NONE.text,
            actions: actions(response.instanceId, type),
            processed: response.processed ? "Yes" : "No",
          };
        })
    );

    return tempData;
  };

  // Function to load more data progressively
  const loadMoreData = async () => {
    if (isLoadingMore || !hasMoreData) return;

    setIsLoadingMore(true);
    try {
      const from = moment(startDate).valueOf();
      const to = moment(endDate).valueOf();

      const promises = [];

      // Only fetch data types that have more pages
      if (paginationTokens.patrols) {
        promises.push(
          queryPatrolsByServiceProviderIdPaginated({
            serviceProviderId,
            from,
            to,
            after: paginationTokens.patrols,
          })
        );
      } else {
        promises.push(Promise.resolve({ items: [], nextToken: null }));
      }

      if (paginationTokens.incidents) {
        promises.push(
          queryIncidentReportsByServiceProviderIdPaginated({
            serviceProviderId,
            from,
            to,
            after: paginationTokens.incidents,
          })
        );
      } else {
        promises.push(Promise.resolve({ items: [], nextToken: null }));
      }

      if (paginationTokens.responses) {
        promises.push(
          queryResponsesByServiceProviderIdPaginated({
            serviceProviderId,
            from,
            to,
            after: paginationTokens.responses,
          })
        );
      } else {
        promises.push(Promise.resolve({ items: [], nextToken: null }));
      }

      const [patrolResult, incidentResult, responseResult] = await Promise.all(promises);

      // Update state with new data
      const newPatrols = patrolResult.items || [];
      const newIncidents = incidentResult.items || [];
      const newResponses = responseResult.items || [];

      // Update patrols
      if (newPatrols.length > 0) {
        setPatrols(prev => ({
          ...prev,
          ...newPatrols.reduce(
            (acc, patrol) => ({ ...acc, [patrol.patrolId]: patrol }),
            {}
          )
        }));
      }

      // Update incidents
      if (newIncidents.length > 0) {
        setIncidents(prev => ({
          ...prev,
          ...newIncidents.reduce(
            (acc, incident) => ({ ...acc, [incident.incidentId]: incident }),
            {}
          )
        }));
      }

      // Update responses
      if (newResponses.length > 0) {
        setResponses(prev => ({
          ...prev,
          ...newResponses.reduce(
            (acc, response) => ({ ...acc, [response.instanceId]: response }),
            {}
          )
        }));
      }

      // Update pagination tokens
      setPaginationTokens(prev => ({
        patrols: patrolResult.nextToken || null,
        incidents: incidentResult.nextToken || null,
        responses: responseResult.nextToken || null,
      }));

      // Check if we have more data to load
      const hasMore = patrolResult.nextToken || incidentResult.nextToken || responseResult.nextToken;
      setHasMoreData(hasMore);

      // Process and add new data to display
      const newDisplayData = processDataForDisplay(newPatrols, newIncidents, newResponses);
      const updatedData = [...data, ...newDisplayData];
      setData(updatedData);

      // Update cache with new data
      if (cacheKey) {
        setDataCache(prev => ({
          ...prev,
          [cacheKey]: {
            ...prev[cacheKey],
            displayData: updatedData,
            patrols: { ...patrolData, ...newPatrols.reduce((acc, patrol) => ({ ...acc, [patrol.patrolId]: patrol }), {}) },
            incidents: { ...incidentData, ...newIncidents.reduce((acc, incident) => ({ ...acc, [incident.incidentId]: incident }), {}) },
            responses: { ...responseData, ...newResponses.reduce((acc, response) => ({ ...acc, [response.instanceId]: response }), {}) },
            paginationTokens: {
              patrols: patrolResult.nextToken || null,
              incidents: incidentResult.nextToken || null,
              responses: responseResult.nextToken || null,
            },
            hasMoreData: hasMore,
            currentPage: currentPage + 1,
          }
        }));
      }

      setCurrentPage(prev => prev + 1);

    } catch (error) {
      console.error('Error loading more data:', error);
      enqueueSnackbar('Error loading more data', { variant: 'error' });
    } finally {
      setIsLoadingMore(false);
    }
  };

  useEffect(() => {
    const temp = { patrols: [], responses: [], incidents: [] };
    for (const item of items) {
      if (Object.keys(patrolData).includes(item))
        temp.patrols.push(patrolData[item]);
      if (Object.keys(responseData).includes(item))
        temp.responses.push(responseData[item]);
      if (Object.keys(incidentData).includes(item))
        temp.incidents.push(incidentData[item]);
    }
    setInput(temp);
  }, [items, patrolData, responseData, incidentData]);



  const actions = (id, variant) => {
    let clickHandler;
    if (variant === "Daily Activity" || variant === "VIP Report") {
      clickHandler = () => {
        setDailyActivityId(id);
      };
    } else if (variant === "Incident") {
      clickHandler = () => {
        setIncidentId(id);
      };
    } else if (variant === "Response") {
      clickHandler = () => {
        setResponseId(id);
      };
    }
    return (
      <span className={classes.actions}>
        <IconButton onClick={clickHandler}>
          <EyeIcon />
        </IconButton>
      </span>
    );
  };

  const sendClientReport = async () => {
    setShowSendClientReportAlert(false);
    setClientReportLoading(true);
    try {
      const serviceProvider = await getServiceProvider(serviceProviderId);
      const urls = await sendReports({
        clients: Object.values(clients),
        dars: input.patrols,
        endDate,
        incidents: input.incidents,
        propertiesById: properties,
        responses: input.responses,
        serviceProvider,
        startDate,
      });
      enqueueSnackbar(`${urls.length} reports sent to clients`, {
            variant: "success",
          });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        typeof error === "string" ? error : "An unexpected error occurred.",
        { variant: "error" }
      );
    }
    setClientReportLoading(false);
  };

  const onSearch = async () => {
    const from = moment(startDate).valueOf();
    const to = moment(endDate).valueOf();

    if (from >= to) {
      return enqueueSnackbar("Start must be before end date", {
        variant: "error",
      });
    }

    if (!serviceProviderId) {
      console.error('serviceProviderId is undefined');
      return enqueueSnackbar("Service provider ID is missing", {
        variant: "error",
      });
    }

    // Check if we have any filters applied
    const hasFilters = (exportFilters.propertyName && exportFilters.propertyName.trim()) ||
                      (exportFilters.userName && exportFilters.userName.trim()) ||
                      (exportFilters.reportType && exportFilters.reportType !== "all");

    // If export mode is enabled OR we have filters, use optimized queries
    if (exportMode || hasFilters) {
      return onUnifiedSearch();
    }

    // Regular search mode
    // Create cache key based on search parameters
    const newCacheKey = `${serviceProviderId}-${from}-${to}`;

    // Check if we have cached data for this search
    if (dataCache[newCacheKey] && cacheKey === newCacheKey) {
      const cachedData = dataCache[newCacheKey];
      setData(cachedData.displayData);
      setPatrols(cachedData.patrols);
      setIncidents(cachedData.incidents);
      setResponses(cachedData.responses);
      setPaginationTokens(cachedData.paginationTokens);
      setHasMoreData(cachedData.hasMoreData);
      setCurrentPage(cachedData.currentPage);
      setItems([]);
      return;
    }

    setSearchLoading(true);
    setData([]); // Clear existing data
    setCurrentPage(0);
    setHasMoreData(false);
    setIsLoadingMore(false);

    try {
      // Load first batch of data using paginated queries
      const promises = [
        queryPatrolsByServiceProviderIdPaginated({
          serviceProviderId,
          from,
          to,
        }),
        queryIncidentReportsByServiceProviderIdPaginated({
          serviceProviderId,
          from,
          to,
        }),
        queryResponsesByServiceProviderIdPaginated({
          serviceProviderId,
          from,
          to,
        }),
      ];

      const [patrolResult, incidentResult, responseResult] = await Promise.all(promises);

      // Extract data and pagination tokens
      const patrols = patrolResult.items || [];
      const incidents = incidentResult.items || [];
      const responses = responseResult.items || [];

      // Update pagination tokens
      const newPaginationTokens = {
        patrols: patrolResult.nextToken,
        incidents: incidentResult.nextToken,
        responses: responseResult.nextToken,
      };
      setPaginationTokens(newPaginationTokens);

      // Check if we have more data to load
      const hasMore = patrolResult.nextToken || incidentResult.nextToken || responseResult.nextToken;
      setHasMoreData(hasMore);

      // Set initial data
      const newPatrols = patrols.reduce(
          (acc, patrol) => ({ ...acc, [patrol.patrolId]: patrol }),
          {}
      );
      const newIncidents = incidents.reduce(
          (acc, incident) => ({ ...acc, [incident.incidentId]: incident }),
          {}
      );
      const newResponses = responses.reduce(
          (acc, response) => ({ ...acc, [response.instanceId]: response }),
          {}
      );

      setPatrols(newPatrols);
      setIncidents(newIncidents);
      setResponses(newResponses);

      // Process and display first batch
      const initialDisplayData = processDataForDisplay(patrols, incidents, responses);
      setData(initialDisplayData);
      setItems([]);

      // Cache the data
      manageCache(newCacheKey, {
        displayData: initialDisplayData,
        patrols: newPatrols,
        incidents: newIncidents,
        responses: newResponses,
        paginationTokens: newPaginationTokens,
        hasMoreData: hasMore,
        currentPage: 1,
      });
      setCacheKey(newCacheKey);

      setCurrentPage(1);

    } catch (e) {
      console.error(e);
      enqueueSnackbar(appsyncError(e), { variant: "error" });
    }
    setSearchLoading(false);
  };

  // Export-only search for large datasets (100k+ records)
  const onUnifiedSearch = async () => {
    const from = moment(startDate).valueOf();
    const to = moment(endDate).valueOf();

    if (from >= to) {
      return enqueueSnackbar("Start must be before end date", {
        variant: "error",
      });
    }

    // Warn about large datasets for export mode
    if (exportMode) {
      const daysDiff = moment(to).diff(moment(from), 'days');
      if (daysDiff > 30) {
        const confirmed = window.confirm(
          `You are about to export data for ${daysDiff} days. This may take several minutes and generate a large PDF. Continue?`
        );
        if (!confirmed) return;
      }
    }

    setSearchLoading(true);

    try {
      const [patrolsResult, incidentsResult, instancesResult] = await Promise.all([
        queryPatrolsOptimized(exportFilters, from, to),
        queryIncidentsOptimized(exportFilters, from, to),
        queryInstancesOptimized(exportFilters, from, to)
      ]);

      // Apply report type filtering if specified
      let filteredPatrols = patrolsResult.items;
      let filteredIncidents = incidentsResult.items;
      let filteredResponses = instancesResult.items;

      if (exportFilters.reportType) {
        if (exportFilters.reportType === "patrols") {
          filteredIncidents = [];
          filteredResponses = [];
        } else if (exportFilters.reportType === "incidents") {
          filteredPatrols = [];
          filteredResponses = [];
        } else if (exportFilters.reportType === "responses") {
          filteredPatrols = [];
          filteredIncidents = [];
        }
      }

      const totalRecords = filteredPatrols.length + filteredIncidents.length + filteredResponses.length;

      if (exportMode) {
        // Generate PDF using the exact same logic as manual download
        const fileName = `report_${moment().format("YYYY:MM:DD_HH:mm")}_${totalRecords}records.pdf`;

        try {
          // Use the exact same generatePDF function as handleExport
          await generatePDF(
            Array.isArray(filteredPatrols) ? filteredPatrols : [],
            Array.isArray(filteredIncidents) ? filteredIncidents : [],
            Array.isArray(filteredResponses) ? filteredResponses : [],
            fileName
          );

          enqueueSnackbar(`PDF exported successfully: ${fileName} (${totalRecords} records)`, {
            variant: 'success',
          });
        } catch (pdfError) {
          console.error('PDF generation failed:', pdfError);
          enqueueSnackbar(`PDF generation failed: ${pdfError.message || 'Unknown error'}`, {
            variant: 'error',
          });
          throw pdfError;
        }
      } else {
        // Display mode: Show results in browser

        // Convert to the format expected by the display components (same as regular search)
        const newPatrols = filteredPatrols.reduce(
          (acc, patrol) => ({ ...acc, [patrol.patrolId]: patrol }),
          {}
        );
        const newIncidents = filteredIncidents.reduce(
          (acc, incident) => ({ ...acc, [incident.incidentId]: incident }),
          {}
        );
        const newResponses = filteredResponses.reduce(
          (acc, response) => ({ ...acc, [response.instanceId]: response }),
          {}
        );

        setPatrols(newPatrols);
        setIncidents(newIncidents);
        setResponses(newResponses);

        // Process and display data using the same function as regular search
        const displayData = processDataForDisplay(filteredPatrols, filteredIncidents, filteredResponses);
        setData(displayData);
        setItems([]); // Clear items array like regular search
        setCurrentPage(1); // Set to 1 like regular search
        setHasMoreData(false); // No pagination for filtered results
        setPaginationTokens({ patrols: null, incidents: null, responses: null });
      }

    } catch (e) {
      console.error('Search error:', e);
      enqueueSnackbar(appsyncError(e), { variant: "error" });
    } finally {
      setSearchLoading(false);
    }
  };

  const onExportSearch = async () => {
    const from = moment(startDate).valueOf();
    const to = moment(endDate).valueOf();

    if (from >= to) {
      return enqueueSnackbar("Start must be before end date", {
        variant: "error",
      });
    }

    // Warn about large datasets
    const daysDiff = moment(to).diff(moment(from), 'days');
    if (daysDiff > 30) {
      const confirmed = window.confirm(
        `You are about to export data for ${daysDiff} days. This may take several minutes and generate a large PDF. Continue?`
      );
      if (!confirmed) return;
    }

    setSearchLoading(true);

    try {

      const [patrolsResult, incidentsResult, instancesResult] = await Promise.all([
        queryPatrolsOptimized(exportFilters, from, to),
        queryIncidentsOptimized(exportFilters, from, to),
        queryInstancesOptimized(exportFilters, from, to)
      ]);

      // Apply report type filtering if specified
      let filteredPatrols = patrolsResult.items;
      let filteredIncidents = incidentsResult.items;
      let filteredResponses = instancesResult.items;

      if (exportFilters.reportType) {
        if (exportFilters.reportType === "patrols") {
          filteredIncidents = [];
          filteredResponses = [];
        } else if (exportFilters.reportType === "incidents") {
          filteredPatrols = [];
          filteredResponses = [];
        } else if (exportFilters.reportType === "responses") {
          filteredPatrols = [];
          filteredIncidents = [];
        }
      }

      const totalRecords = filteredPatrols.length + filteredIncidents.length + filteredResponses.length;
      // Generate PDF using the exact same logic as manual download
      const fileName = `report_${moment().format("YYYY:MM:DD_HH:mm")}_${totalRecords}records.pdf`;

      try {
        // Use the exact same generatePDF function as handleExport
        await generatePDF(
          Array.isArray(filteredPatrols) ? filteredPatrols : [],
          Array.isArray(filteredIncidents) ? filteredIncidents : [],
          Array.isArray(filteredResponses) ? filteredResponses : [],
          fileName
        );

        enqueueSnackbar(`PDF exported successfully: ${fileName} (${totalRecords} records)`, {
          variant: 'success',
        });
      } catch (pdfError) {
        console.error('PDF generation failed:', pdfError);
        enqueueSnackbar(`PDF generation failed: ${pdfError.message || 'Unknown error'}`, {
          variant: 'error',
        });
        throw pdfError;
      }

    } catch (e) {
      console.error('Export error:', e);
      enqueueSnackbar(appsyncError(e), { variant: "error" });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const fileName = `report_${moment().format("YYYY:MM:DD_HH:mm")}`;
      await generatePDF(input.patrols, input.incidents, input.responses, fileName);

          enqueueSnackbar("Download successful", {
            variant: "success",
          });
    } catch (e) {
      console.error(e);
      enqueueSnackbar("An unexpected error occurred. Please try again.", {
        variant: "error",
      });
    } finally {
      setExportLoading(false);
    }
  };
  const runMarkAsProcessed = async () => {
    setMarkAsProcessedLoading(true);
    try {
      const dailyActivityReportIds = input.patrols.map(
        (patrol) => patrol.patrolId
      );
      const incidentReportIds = input.incidents.map(
        (incident) => incident.incidentId
      );
      const instanceIds = input.responses.map(
        (response) => response.instanceId
      );
      const responseKeys = input.responses.map((response) => ({
        instanceId: response.instanceId,
        propertyId: response.propertyId,
      }));
      await markAsProcessed({
        dailyActivityReportIds,
        incidentReportIds,
        responseKeys,
      });
      setData(
        data.map((dataItem) => {
          if (
            dailyActivityReportIds.includes(dataItem.id) ||
            incidentReportIds.includes(dataItem.id) ||
            instanceIds.includes(dataItem.id)
          ) {
            return {
              ...dataItem,
              processed: "Yes",
            };
          } else {
            return dataItem;
          }
        })
      );
      enqueueSnackbar(
        `${items.length} item${
          items.length > 1 ? "s" : ""
        } marked as processed`,
        {
          variant: "success",
        }
      );
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
    setMarkAsProcessedLoading(false);
  };
  const runMarkAsNotProcessed = async () => {
    setMarkAsNotProcessedLoading(true);
    try {
      const dailyActivityReportIds = input.patrols.map(
        (patrol) => patrol.patrolId
      );
      const incidentReportIds = input.incidents.map(
        (incident) => incident.incidentId
      );
      const instanceIds = input.responses.map(
        (response) => response.instanceId
      );
      const responseKeys = input.responses.map((response) => ({
        instanceId: response.instanceId,
        propertyId: response.propertyId,
      }));
      await markAsNotProcessed({
        dailyActivityReportIds,
        incidentReportIds,
        responseKeys,
      });
      setData(
        data.map((dataItem) => {
          if (
            dailyActivityReportIds.includes(dataItem.id) ||
            incidentReportIds.includes(dataItem.id) ||
            instanceIds.includes(dataItem.id)
          ) {
            return {
              ...dataItem,
              processed: "No",
            };
          } else {
            return dataItem;
          }
        })
      );
      enqueueSnackbar(
        `${items.length} item${
          items.length > 1 ? "s" : ""
        } marked as not processed`,
        {
          variant: "success",
        }
      );
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
    setMarkAsNotProcessedLoading(false);
  };

  return (
    <div className={classes.root}>
      <div className={classes.scrollable}>
        <Paper className={classes.paper}>
            <Grid container spacing={3}>
              <Grid item s={12} xs={12}>
                <Header label={"Reports"} />
              </Grid>
              <Grid item xs={5}>
                <TextField
                  label="Start Date"
                  type="datetime-local"
                  value={moment(startDate).format('YYYY-MM-DDTHH:mm')}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    if (!isNaN(newDate.getTime())) {
                      setStartDate(newDate);
                    }
                  }}
                  inputProps={{
                    max: moment().format('YYYY-MM-DDTHH:mm')
                  }}
                  variant="outlined"
                  fullWidth
                  disabled={searchLoading}
                />
              </Grid>
              <Grid item xs={5}>
                <TextField
                  label="End Date"
                  type="datetime-local"
                  value={moment(endDate).format('YYYY-MM-DDTHH:mm')}
                  onChange={(e) => {
                    const newDate = new Date(e.target.value);
                    if (!isNaN(newDate.getTime())) {
                      setEndDate(newDate);
                    }
                  }}
                  inputProps={{
                    min: moment(startDate).format('YYYY-MM-DDTHH:mm'),
                    max: moment(startDate).add(1, 'M').format('YYYY-MM-DDTHH:mm')
                  }}
                  variant="outlined"
                  fullWidth
                  disabled={searchLoading}
                />
              </Grid>
              <Grid item xs={2} style={{ display: "flex" }}>
                <LoadingButton
                  fullWidth
                  isLoading={searchLoading}
                  color={exportMode ? "secondary" : "primary"}
                  variant={"contained"}
                  onClick={onSearch}
                  disabled={searchLoading}
                >
                  {exportMode ? "Export to PDF" : "Search"}
                </LoadingButton>
              </Grid>

              {/* Export Mode Toggle and Filters */}
              <Grid item xs={12} style={{ marginTop: 16 }}>
                <Paper style={{ padding: 16, marginBottom: 16 }}>
                  <Typography variant="h6" style={{ marginBottom: 16, fontWeight: 'bold' }}>
                    Search Options
                  </Typography>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <FormControl component="fieldset">
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={exportMode}
                            onChange={(e) => setExportMode(e.target.checked)}
                            style={{ marginRight: 8 }}
                          />
                          <Typography variant="body1">
                            Export Mode
                          </Typography>
                        </label>
                        <Typography variant="caption" color="textSecondary">
                          {"Export all matching records directly to PDF format" }
                        </Typography>
                      </FormControl>
                    </Grid>

                    {/* Filters - Always visible */}
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Filter by Property Name"
                        value={exportFilters.propertyName}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, propertyName: e.target.value }))}
                        variant="outlined"
                        fullWidth
                        size="small"
                        placeholder="Enter property name..."
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Filter by User Name"
                        value={exportFilters.userName}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, userName: e.target.value }))}
                        variant="outlined"
                        fullWidth
                        size="small"
                        placeholder="Enter user name..."
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel>Report Type</InputLabel>
                        <Select
                          value={exportFilters.reportType}
                          onChange={(e) => setExportFilters(prev => ({ ...prev, reportType: e.target.value }))}
                          label="Report Type"
                        >
                          <MenuItem value="all">All Reports</MenuItem>
                          <MenuItem value="patrols">Daily Activities & VIP Reports</MenuItem>
                          <MenuItem value="incidents">Incidents Only</MenuItem>
                          <MenuItem value="responses">Responses Only</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              <Grid item lg={12} xs={12}>
                {exportMode ? (
                  <Paper style={{ padding: 32, textAlign: 'center', marginTop: 16 }}>
                    <Typography variant="h6" gutterBottom>
                      Export Mode Active
                    </Typography>
                    <Typography variant="body1" color="textSecondary" paragraph>
                      When you click "Export to PDF", all reports matching your date range and filters will be exported directly to a PDF file. Filters are automatically applied when any text is entered.
                    </Typography>
                  </Paper>
                ) : (
                  <>
                <ReportsTable
                  data={data}
                  serviceProviderId={serviceProviderId}
                  onSelectionChange={(e) => setItems([...e])}
                  selected={items}
                />

                    {/* Progressive Loading UI */}
                    {data.length > 0 && (
                      <div style={{ marginTop: 16, textAlign: 'center' }}>
                        <div style={{ marginBottom: 8 }}>
                          <Typography variant="body2" color="textSecondary">
                            Showing {data.length} reports
                          </Typography>
                        </div>

                        {hasMoreData && (
                          <div>
                            <LoadingButton
                              variant="outlined"
                              color="primary"
                              onClick={loadMoreData}
                              isLoading={isLoadingMore}
                              disabled={isLoadingMore}
                              style={{ marginRight: 16 }}
                            >
                              Load More
                            </LoadingButton>

                            {isLoadingMore && (
                              <Typography variant="body2" color="textSecondary">
                                Loading more reports...
                              </Typography>
                            )}
                          </div>
                        )}

                        {!hasMoreData && data.length > 0 && (
                          <Typography variant="body2" color="textSecondary">
                            All available reports loaded ({data.length} total)
                          </Typography>
                        )}

                        {data.length >= maxReports && (
                          <Typography variant="body2" color="warning.main" style={{ marginTop: 8 }}>
                            Report amount can quickly exceed browser capacity,
                            use Export Mode and/or apply filters to narrow results.
                          </Typography>
                        )}
                      </div>
                    )}
                  </>
                )}
              </Grid>
              {!exportMode && (
              <Grid
                item
                lg={12}
                xs={12}
                style={{ display: "flex", justifyContent: "flex-end" }}
              >
                {items.length > maxReports && (
                  <Typography
                    variant="h6"
                    style={{
                      marginRight: 16,
                    }}
                  >
                    {`You can export a max of ${maxReports} reports`}
                  </Typography>
                )}
                {exportLoading && (
                  <Typography
                    variant="h6"
                    style={{
                      marginRight: 16,
                    }}
                  >
                    The report may take several minutes to download.
                  </Typography>
                )}
                <LoadingButton
                  className={classes.button}
                  isLoading={clientReportLoading}
                  color={"primary"}
                  variant={"contained"}
                  onClick={() => setShowSendClientReportAlert(true)}
                  disabled={items.length === 0 || items.length > maxReports}
                >
                  {`Send To Clients`}
                </LoadingButton>
                <LoadingButton
                  className={classes.button}
                  isLoading={exportLoading}
                  color={"primary"}
                  variant={"contained"}
                  onClick={handleExport}
                  disabled={items.length === 0 || items.length > maxReports}
                >
                  {`Download ${items.length}`}
                </LoadingButton>
                <LoadingButton
                  className={classes.button}
                  isLoading={markAsProcessedLoading}
                  color="primary"
                  variant="contained"
                  onClick={runMarkAsProcessed}
                  disabled={items.length === 0 || items.length > maxReports}
                >
                  Mark As Processed
                </LoadingButton>
                <LoadingButton
                  className={classes.button}
                  isLoading={markAsNotProcessedLoading}
                  color="primary"
                  variant="contained"
                  onClick={runMarkAsNotProcessed}
                  disabled={items.length === 0 || items.length > maxReports}
                >
                  Mark As Not Processed
                </LoadingButton>
              </Grid>
              )}
            </Grid>
          {dailyActivityId !== null && (
            <ActivityDialog
              onClose={() => setDailyActivityId(null)}
              isOpen
              reportId={dailyActivityId}
              onSaveSuccess={(report) => {
                setPatrols({ ...patrolData, [report.patrolId]: report });
              }}
            />
          )}
          {incidentId !== null && (
            <IncidentDialog
              onClose={() => setIncidentId(null)}
              isOpen
              reportId={incidentId}
              onSaveSuccess={(report) =>
                setIncidents({ ...incidentData, [report.incidentId]: report })
              }
            />
          )}
          {responseId !== null && (
            <ResponseDialog
              onClose={() => setResponseId(null)}
              isOpen
              instanceId={responseId}
            />
          )}
          <AlertDialog
            isOpen={showSendClientReportAlert}
            onClose={() => setShowSendClientReportAlert(false)}
            onNo={() => setShowSendClientReportAlert(false)}
            onYes={sendClientReport}
            title={"Make reports viewable to client?"}
            content={
              "This will send a pdf version of the selected reports to your client, if you have associated the property with a client."
            }
          />
        </Paper>
      </div>
    </div>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    flex: 1,
    position: "relative",
  },
  scrollable: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    overflowX: "hidden",
    overflowY: "auto",
  },
  paper: {
    margin: theme.spacing(3),
    padding: theme.spacing(3),
    "& .MuiPopover-root": {
      zIndex: 9999,
    },
    "& .MuiPickersBasePicker-pickerView": {
      zIndex: 9999,
    },
  },
  actions: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  button: {
    margin: theme.spacing(1),
  },
}));

const mapStateToProps = (state) => ({
  serviceProviderId: state.auth.user.attributes["custom:service_provider_id"],
  properties: state.properties.byId,
  users: state.users.byId,
  clients: state.clients.entities,
});

export default withSnackbar(connect(mapStateToProps)(ReportsContent));
